import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import type { MenuProps } from 'antd';
import {
  Button,
  Checkbox,
  Dropdown,
  Pagination,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { TableColumnsType as ColumnsType } from 'antd';
import {
  DownOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  ReloadOutlined,
} from '@ant-design/icons';

import { formatters, useModal } from 'services';
import {
  closePositions,
  computeIndividualReports,
  CREATING,
  deleteSession,
  FINISHED,
  finishSession,
  listOpenOrders,
  PLAYING,
  playPauseSession,
  SESSION_STATUSES,
  selectors,
  fetchPaginatedSessions,
  SCENARIO_TYPES,
  RISK_APPROACHES,
  Session,
  cloneSession,
  NEVER_AUTO_TRADED,
  fetchSessionReplayDefaults,
  fetchReplayTargetKlasses,
  updateSessionBillable,
} from 'state';
import { columnSearchWidget } from 'components';
import ActionModal from './ActionModal';
import CloneSessionModal from './cloneSessionModal';
import PromoteBlueprintModal from './PromoteBlueprintModal';
import CreateSessionStudio from '../../TrainerDashboard/create_session_v2/CreateSessionStudio';
import useSessionPlayerCounts from './useSessionPlayerCounts';
import SessionCountsSummary from './SessionCountsSummary';
import useRealtimeSessionCounts from './useRealtimeSessionCounts';

export type SessionFilters = {
  status?: string[];
  klass_name?: string[];
  trainer_name?: string[];
  sorter: SessionSorter;
};

export type SessionSorter = {
  field?: string;
  order?: 'ascend' | 'descend';
};

const PAGE_SIZE = 20;
const PAGE_NUMBER = 1;

const SessionsList: React.FC = () => {
  const dispatch = useDispatch();
  const {
    items: sessions,
    waiting,
    count,
    page_number,
    page_size,
    revision = 0,
  } = useSelector(selectors.paginatedSessions);

  // Move redux state to local state
  useEffect(() => {
    setPageNumber(page_number || PAGE_NUMBER);
    setPageSize(page_size || PAGE_SIZE);
  }, [page_number, page_size]);

  // Action dropdown validation
  const [Modal, showModal] = useModal(ActionModal);
  const [cloneModal, showCloneModal] = useModal(CloneSessionModal);
  const [promoteModal, showPromoteModal] = useModal(PromoteBlueprintModal);

  // Cross-trainer "Replay into..." (reuses the trainer CreateSessionStudio modal
  // with a Trainer -> Class destination picker).
  const replayDefaults = useSelector(selectors.replaySessionDefaults);
  const replaySourceId = useSelector(selectors.replaySessionSourceId);
  const replayTargetKlasses = useSelector(selectors.replayTargetKlasses);
  const [replayVisible, setReplayVisible] = useState(false);

  // Pagination
  const [pageNumber, setPageNumber] = useState(PAGE_NUMBER);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  // Request params
  const [filters, setFilters] = useState<SessionFilters>({
    sorter: {
      field: 'id',
      order: 'descend',
    },
  });

  const params = JSON.stringify(filters);

  useEffect(() => {
    dispatch(
      fetchPaginatedSessions({
        params,
        page_number: pageNumber,
        page_size: pageSize,
      }),
    );
  }, [dispatch, pageNumber, pageSize, params]);

  const playerCounts = useSessionPlayerCounts(
    sessions.map(session => session.id),
    revision,
    waiting,
  );
  const realtimeCounts = useRealtimeSessionCounts();

  const countTitle = (label: string, explanation: string) => (
    <Space size={4}>
      {label}
      <Tooltip title={explanation} trigger={['hover', 'focus']}>
        <InfoCircleOutlined
          aria-label={`About ${label}`}
          tabIndex={0}
          style={{ color: '#8c8c8c', fontSize: 12 }}
        />
      </Tooltip>
    </Space>
  );

  const statusFilters = Object.entries(SESSION_STATUSES).map(([key, value]) => {
    return { text: value, value: parseInt(key) };
  });

  const scenarioFilters = Object.entries(SCENARIO_TYPES).map(([key, value]) => {
    return { text: value, value: parseInt(key) };
  });

  const approachFilters = Object.entries(RISK_APPROACHES).map(
    ([key, value]) => {
      return { text: value, value: parseInt(key) };
    },
  );

  const columns: ColumnsType<Session> = [
    {
      title: 'Id',
      dataIndex: 'id',
      key: 'id',
      sorter: () => 0,
      defaultSortOrder: 'descend',
      width: '50px',
    },
    {
      title: 'Class',
      dataIndex: 'klass_name',
      key: 'klass_name',
      sorter: () => 0,
      ...columnSearchWidget({ dataIndex: 'klass_name' }),
    },
    {
      title: 'Trainer',
      dataIndex: 'trainer_name',
      key: 'trainer_name',
      sorter: () => 0,
      ...columnSearchWidget({ dataIndex: 'trainer_name' }),
    },
    {
      title: 'Create Date',
      dataIndex: 'create_date',
      key: 'create_date',
      width: '200px',
      render: text => formatters.dateWithTime(text),
      sorter: () => 0,
    },
    {
      title: 'Scenario',
      dataIndex: 'scenario_type',
      key: 'scenario_type',
      render: text => SCENARIO_TYPES[text as 0 | 1],
      filters: scenarioFilters,
    },
    {
      title: 'Approach',
      dataIndex: 'risk_approach',
      key: 'risk_approach',
      render: text => RISK_APPROACHES[text as 0 | 1],
      filters: approachFilters,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: () => 0,
      render: (status: 0 | 1 | 2 | 3) => SESSION_STATUSES[status],
      filters: statusFilters,
      width: '100px',
    },
    {
      title: 'Assets',
      dataIndex: 'underlying_assets',
      key: 'underlying_assets',
      width: '50px',
      sorter: () => 0,
      // Older/incomplete rows may omit asset IDs; show unavailable rather than a false zero or crash.
      render: assets => (Array.isArray(assets) ? assets.length : '—'),
    },
    {
      title: countTitle(
        'Players',
        'Total players in this session, including unused players.',
      ),
      key: 'players_count',
      align: 'right',
      width: 90,
      render: (_, record) =>
        playerCounts.data?.counts[record.id]?.players ?? '—',
    },
    {
      title: countTitle('Active', 'Players with trading or account activity.'),
      key: 'active_count',
      align: 'right',
      width: 85,
      render: (_, record) =>
        playerCounts.data?.counts[record.id]?.active ?? '—',
    },
    {
      title: (
        <Tooltip title="Players connected to this session when counts were refreshed. Each player counts once; trainers and admins are excluded.">
          Online
        </Tooltip>
      ),
      key: 'online_count',
      align: 'right',
      width: 75,
      render: (_, record) =>
        playerCounts.data?.counts[record.id]?.online ?? '—',
    },
    {
      title: 'Actions',
      dataIndex: 'status',
      width: '50px',
      render: function Status(_status: 0 | 1 | 2 | 3, record) {
        const menuItems: MenuProps['items'] = [
          {
            key: 'play-pause',
            label: record.status === PLAYING ? 'Pause' : 'Play',
            disabled:
              record.status === FINISHED ||
              record.auto_trader_status != NEVER_AUTO_TRADED,
          },
          {
            key: 'end',
            label: 'End',
            danger: true,
            disabled: record.status === FINISHED || record.status === CREATING,
          },
          {
            key: 'close-positions',
            label: 'Close positions (Real time only)',
          },
          {
            key: 'compute-reports',
            label: 'Compute Reports',
            disabled: record.status === CREATING,
          },
          {
            key: 'open-orders',
            label: 'Open Orders',
          },
          {
            key: 'clone-session',
            label: 'Clone session',
            disabled: record.status !== FINISHED,
          },
          {
            key: 'replay-into',
            label: 'Replay into…',
            // Per-sector-alpha replay is not yet supported (backend blocks it).
            disabled: record.per_sector_alpha,
          },
          {
            key: 'save-as-blueprint',
            label: 'Save as Blueprint',
          },
          {
            key: 'delete-session',
            label: 'Delete session',
            danger: true,
          },
        ];

        if (record.tandem) {
          menuItems.forEach(item => {
            if (item && item.key !== 'open-orders' && 'disabled' in item)
              item.disabled = true;
            else if (item && item.key !== 'open-orders')
              Object.assign(item, { disabled: true });
          });
        }

        const handleMenuClick: MenuProps['onClick'] = ({ key, domEvent }) => {
          domEvent.stopPropagation();

          switch (key) {
            case 'play-pause':
              dispatch(
                playPauseSession({
                  id: record.id,
                  current_status: record.status,
                }),
              );
              break;
            case 'end':
              showModal({
                title: `End Session ${record.id}`,
                action: finishSession({ id: record.id }),
                message: 'Are you sure you want to end this session?',
              });
              break;
            case 'close-positions':
              showModal({
                title: 'Close positions',
                action: closePositions({ session_id: record.id }),
                message: 'Are you sure you want to close all positions?',
              });
              break;
            case 'compute-reports':
              showModal({
                title: 'Compute individual reports',
                action: computeIndividualReports({
                  session_id: record.id,
                }),
                message:
                  'Are you sure you want to compute reports?\n' +
                  'This will delete previous individual reports for session.',
              });
              break;
            case 'open-orders':
              dispatch(listOpenOrders({ session_id: record.id }));
              break;
            case 'clone-session':
              showCloneModal({
                session_id: record.id,
              });
              break;
            case 'replay-into':
              // Prefill from the chosen source session and load the destination
              // trainer/class options, then open the reused create modal.
              dispatch(fetchSessionReplayDefaults({ id: record.id }));
              dispatch(fetchReplayTargetKlasses({}));
              setReplayVisible(true);
              break;
            case 'save-as-blueprint':
              showPromoteModal({ session_id: record.id });
              break;
            case 'delete-session':
              showModal({
                title: `Delete session ${record.id}`,
                action: deleteSession({
                  id: record.id,
                }),
                message: 'Are you sure you want to delete this session?',
              });
              break;
          }
        };

        return (
          <Dropdown
            menu={{ items: menuItems, onClick: handleMenuClick }}
            placement="bottomRight"
            trigger={['click']}
            getPopupContainer={triggerNode =>
              triggerNode.parentElement ?? document.body
            }
          >
            <span>
              <Button>
                <DownOutlined />
              </Button>
            </span>
          </Dropdown>
        );
      },
    },
    {
      title: 'Select',
      key: 'select',
      width: '80px',
      render: function Action(_text, record) {
        return (
          <Space key={record.id} size="middle" wrap>
            <Link to={`/dashboard/sessions/${record.id}/players`}>
              <Button>Select</Button>
            </Link>
            {record.tandem && (
              <Space direction="vertical" size={2}>
                <Tag color="geekblue">
                  Tandem {record.tandem.pair_id.slice(0, 8)} ·{' '}
                  {record.tandem.side}
                </Tag>
                <Link to={record.tandem.control_url}>
                  <Button size="small">Open tandem</Button>
                </Link>
              </Space>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Billable?',
      dataIndex: 'billable',
      key: 'billable',
      width: '80px',
      align: 'center',
      render: function Billable(billable: boolean | undefined, record) {
        return (
          <Checkbox
            checked={Boolean(billable)}
            disabled={!!record.tandem}
            aria-label={`Session ${record.id} billable`}
            onChange={event =>
              dispatch(
                updateSessionBillable({
                  session_id: record.id,
                  billable: event.target.checked,
                }),
              )
            }
          />
        );
      },
    },
  ];

  return (
    <div>
      <Table
        rowKey="id"
        dataSource={sessions}
        bordered
        pagination={false}
        scroll={{ x: 'max-content' }}
        title={() => (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <Typography.Title level={4} style={{ margin: 0 }}>
              {waiting ? (
                <>
                  Loading <LoadingOutlined style={{ color: '#108ee9' }} />
                </>
              ) : (
                <>Sessions ({count})</>
              )}
            </Typography.Title>
            <Space size={8} wrap>
              <Typography.Text
                type="secondary"
                style={{ fontSize: 12 }}
                aria-live="polite"
              >
                {playerCounts.error
                  ? playerCounts.data
                    ? 'Refresh failed · showing previous counts'
                    : 'Counts unavailable'
                  : playerCounts.data?.online_available === false
                    ? 'Online unavailable'
                    : ''}
                {playerCounts.data &&
                  ` ${playerCounts.error || !playerCounts.data.online_available ? '· ' : ''}Updated ${new Date(playerCounts.data.as_of).toLocaleTimeString()}`}
              </Typography.Text>
              <SessionCountsSummary
                data={realtimeCounts.data}
                error={realtimeCounts.error}
              />
              <Button
                size="small"
                icon={<ReloadOutlined />}
                loading={playerCounts.loading || realtimeCounts.loading}
                disabled={waiting}
                onClick={() => {
                  playerCounts.refresh();
                  realtimeCounts.refresh();
                }}
              >
                Refresh counts
              </Button>
            </Space>
          </div>
        )}
        footer={() => (
          <Pagination
            pageSize={pageSize}
            total={count || 0}
            current={pageNumber}
            showSizeChanger
            pageSizeOptions={[10, 20, 50, 100]}
            onChange={page => {
              setPageNumber(page);
            }}
            onShowSizeChange={(page, size) => {
              setPageNumber(PAGE_NUMBER);
              setPageSize(size || PAGE_SIZE);
            }}
          />
        )}
        onChange={(pagination, antFilters, antSorter) => {
          const { order, field } = antSorter as never;
          const sorter = order !== undefined ? { order, field } : {};
          setFilters({ ...antFilters, sorter } as never);
        }}
        columns={columns}
      />

      {Modal}
      {cloneModal}
      {promoteModal}

      <CreateSessionStudio
        visible={replayVisible}
        hide={() => {
          setReplayVisible(false);
          // A successful replay creates a session under (possibly) another trainer;
          // refresh the admin list so the new row shows up.
          dispatch(
            fetchPaginatedSessions({
              params,
              page_number: pageNumber,
              page_size: pageSize,
            }),
          );
        }}
        // klassId is unused in admin replay mode (allowKlassSelect drives the target).
        klassId={0}
        replayDefaults={replayDefaults}
        replaySourceSessionId={replaySourceId}
        allowKlassSelect
        klassOptions={replayTargetKlasses}
      />
    </div>
  );
};

export default SessionsList;
